import React, { useMemo, useRef, useEffect } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { COLORS } from "../constants";
import { useDayFactor, useCrowdDensity } from "../context";
import { CROWD_DENSITY_MIN, CROWD_DENSITY_MAX } from "../constants";

/* -------------------------------------------------------------------------- */
/* ProceduralFigure — kept for CharacterRig (user avatar). Single-instance.   */
/* -------------------------------------------------------------------------- */

export function ProceduralFigure({
  robeColor = COLORS.navy,
  sashColor = COLORS.gold,
  skinColor = "#e2c4a0",
}) {
  return (
    <group>
      <mesh position={[0, 0.6, 0]}>
        <coneGeometry args={[0.45, 1.2, 12]} />
        <meshStandardMaterial color={robeColor} roughness={0.85} />
      </mesh>
      <mesh position={[0, 0.95, 0]}>
        <torusGeometry args={[0.32, 0.04, 8, 16]} />
        <meshStandardMaterial color={sashColor} metalness={0.5} roughness={0.4} />
      </mesh>
      <mesh position={[0, 1.25, 0]}>
        <sphereGeometry args={[0.27, 12, 10]} />
        <meshStandardMaterial color={robeColor} roughness={0.85} />
      </mesh>
      <mesh position={[0, 1.58, 0]}>
        <sphereGeometry args={[0.18, 16, 14]} />
        <meshStandardMaterial color={skinColor} roughness={0.85} />
      </mesh>
      <mesh position={[0, 1.74, 0]}>
        <cylinderGeometry args={[0.15, 0.15, 0.05, 12]} />
        <meshStandardMaterial color={robeColor} roughness={0.85} />
      </mesh>
      <mesh position={[0, 1.78, 0]}>
        <boxGeometry args={[0.42, 0.03, 0.42]} />
        <meshStandardMaterial color={robeColor} roughness={0.85} />
      </mesh>
      <mesh position={[0.15, 1.72, 0]}>
        <cylinderGeometry args={[0.02, 0.02, 0.18, 6]} />
        <meshStandardMaterial color={sashColor} metalness={0.4} />
      </mesh>
    </group>
  );
}

/* -------------------------------------------------------------------------- */
/* InstancedMesh-backed crowd. Targets 100 NPCs at day, ~50 at night.          */
/* -------------------------------------------------------------------------- */

const MAX_NPCS = CROWD_DENSITY_MAX; // 150 — InstancedMesh pool size
const MIN_NPCS_NIGHT = 50;
const DAY_NPCS_DEFAULT = 100;

// Four body archetypes — vary height, shoulder width, robe taper. Mix of
// stockier (broad) and slighter (narrow) silhouettes reads as crowd diversity.
const ARCHETYPES = [
  { id: "broad",  height: 1.06, shoulder: 1.12, robeR: 0.46, head: 1.00 },
  { id: "narrow", height: 0.96, shoulder: 0.84, robeR: 0.40, head: 0.94 },
  { id: "tall",   height: 1.18, shoulder: 0.96, robeR: 0.40, head: 0.96 },
  { id: "short",  height: 0.84, shoulder: 1.04, robeR: 0.48, head: 1.00 },
];

// Robe / sash / skin / hair palettes — academic-collegiate, no real schools.
const PALETTES = [
  { robe: COLORS.navy,     sash: COLORS.gold,        skin: "#e2c4a0", hair: "#3d2920" },
  { robe: "#5a1f2b",       sash: COLORS.gold,        skin: "#c39875", hair: "#1a1310" },
  { robe: "#23433a",       sash: COLORS.ivory,       skin: "#d6b48a", hair: "#523321" },
  { robe: COLORS.navyDeep, sash: "#c9a25b",          skin: "#a37b58", hair: "#0d0a08" },
  { robe: "#3a2d5c",       sash: COLORS.gold,        skin: "#dba88a", hair: "#5d3a1e" },
  { robe: COLORS.brick,    sash: COLORS.ivory,       skin: "#b88660", hair: "#2b1c14" },
  { robe: "#1f3a5c",       sash: COLORS.goldBright,  skin: "#e9c7a5", hair: "#7a3a14" },
  { robe: "#4a3b14",       sash: COLORS.ivory,       skin: "#8a6342", hair: "#0b0808" },
];

// Hair / cap variants. Distribution biased toward mortarboard caps.
const HAIRS = ["mortar", "bob", "topknot", "hood", "bare"];
const HAIR_WEIGHTS = [4, 2, 1, 1, 1]; // total = 9
const HAIR_POOL = HAIRS.flatMap((h, i) => Array(HAIR_WEIGHTS[i]).fill(h));

// Night cluster anchors — lamp posts + building entrances.
const NIGHT_ANCHORS = [
  [10, 3], [-10, 3], [3, 10], [3, -10],
  [19, 3], [-19, 3], [3, 19], [3, -19],
  [28, 3], [-28, 3], [3, 28], [3, -28],
  [0, -19], [0, 19], [19, 0], [-19, 0],
];

// Mulberry32 — deterministic PRNG so the crowd is the same on every load.
function mulberry32(seed) {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6d2b79f5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function buildNpcSpecs() {
  const rng = mulberry32(7919);
  const specs = [];
  // Path strollers — NPCs whose day waypoints straddle the two long
  // corridors from the Quad to the West-Wing Baseball diamond and to the
  // South-East Sports Arena. Keeps the routes from ever feeling empty when
  // a visitor walks between the campus centre and either sports zone. We
  // reserve ~24% of the pool for this (36/150 by default).
  const PATH_STROLLER_COUNT = 36;
  const PATHS = [
    // Quad (near origin) → Sports Arena entrance [40, 0, 40].
    { from: [8, 0, 6], to: [38, 0, 38], side: 3.2 },
    { from: [12, 0, 2], to: [40, 0, 30], side: 2.4 },
    // Quad → Baseball diamond entrance [-31, 0, 55] (east point of the
    // diamond, closest to the quad).
    { from: [-8, 0, 8], to: [-30, 0, 52], side: 3.0 },
    { from: [-4, 0, 12], to: [-28, 0, 58], side: 2.6 },
  ];
  for (let i = 0; i < MAX_NPCS; i++) {
    const archIdx = i % ARCHETYPES.length;
    const palIdx = Math.floor(rng() * PALETTES.length);
    const hair = HAIR_POOL[Math.floor(rng() * HAIR_POOL.length)];

    let dayPath;
    if (i < PATH_STROLLER_COUNT) {
      // Path-stroller — sample 3 waypoints along one of the campus paths,
      // with a small perpendicular jitter so pedestrians don't march in a
      // single-file line.
      const pathIdx = i % PATHS.length;
      const p = PATHS[pathIdx];
      const dx = p.to[0] - p.from[0];
      const dz = p.to[2] - p.from[2];
      const len = Math.sqrt(dx * dx + dz * dz);
      // Perpendicular unit vector (rotate 90°) for side jitter
      const nx = -dz / len;
      const nz = dx / len;
      const t0 = rng() * 0.15;
      const t1 = 0.5 + (rng() - 0.5) * 0.2;
      const t2 = 1 - rng() * 0.15;
      dayPath = [t0, t1, t2].map((t) => {
        const jitter = (rng() - 0.5) * p.side;
        return [
          p.from[0] + dx * t + nx * jitter,
          0,
          p.from[2] + dz * t + nz * jitter,
        ];
      });
      // Random direction of travel (from → to or to → from) by shuffling.
      if (rng() < 0.5) dayPath.reverse();
    } else {
      // Original quad-ring loiterers — 3–4 points scattered on the outer ring.
      const wpCount = 3 + Math.floor(rng() * 2);
      dayPath = [];
      for (let k = 0; k < wpCount; k++) {
        const ang = rng() * Math.PI * 2;
        const r = 8 + rng() * 14;
        dayPath.push([Math.cos(ang) * r, 0, Math.sin(ang) * r]);
      }
    }

    // Night anchor + tight cluster waypoints (radius 1.5–3.5).
    const anchor = NIGHT_ANCHORS[Math.floor(rng() * NIGHT_ANCHORS.length)];
    const nightPath = [];
    for (let k = 0; k < 3; k++) {
      const ang = rng() * Math.PI * 2;
      const r = 1.4 + rng() * 2.0;
      nightPath.push([anchor[0] + Math.cos(ang) * r, 0, anchor[1] + Math.sin(ang) * r]);
    }

    const start = dayPath[0];

    specs.push({
      i,
      archIdx,
      palIdx,
      hair,
      // Varied walking speeds — path strollers walk a touch faster than
      // ring loiterers so the corridors feel like they're going somewhere.
      speed: (i < PATH_STROLLER_COUNT ? 0.65 : 0.45) + rng() * 0.35,
      phase: rng() * Math.PI * 2,
      nightActive: i < MIN_NPCS_NIGHT,
      dayPath,
      nightPath,
      startX: start[0],
      startZ: start[2],
    });
  }
  return specs;
}

/* ------------------------- Part local transforms ------------------------- */
// Part offsets are defined in archetype-local space then multiplied by the
// archetype scale. We precompute the matrix per (archetype, part) once.
//
// Local offsets are tuned to match the original ProceduralFigure proportions
// while letting archetype scales shift height/width plausibly.

function buildArchetypeMatrices() {
  const result = ARCHETYPES.map((arch) => {
    const sH = arch.height;
    const sShoulder = arch.shoulder;
    const sHead = arch.head;
    const sRobeR = arch.robeR / 0.45; // baseline 0.45

    // robe cone — original cone radius 0.45, height 1.2 → cy = 0.6
    const robe = composeTRS(
      [0, 0.6 * sH, 0],
      null,
      [sRobeR, sH, sRobeR]
    );
    // sash torus (sits at robe shoulder seam)
    const sash = composeTRS(
      [0, 0.95 * sH, 0],
      null,
      [sRobeR, 1, sRobeR]
    );
    // shoulders sphere
    const shoulders = composeTRS(
      [0, 1.25 * sH, 0],
      null,
      [sShoulder, sShoulder * 0.9, sShoulder]
    );
    // head sphere
    const head = composeTRS([0, 1.58 * sH, 0], null, [sHead, sHead, sHead]);
    // mortarboard disc + plank + tassel
    const capDisc = composeTRS([0, 1.74 * sH, 0], null, [sHead, 1, sHead]);
    const capPlank = composeTRS([0, 1.78 * sH, 0], null, [sHead, 1, sHead]);
    const capTassel = composeTRS(
      [0.15 * sHead, 1.72 * sH, 0],
      null,
      [1, 1, 1]
    );
    // hair variants (only one is used per NPC; the rest get zero-scale)
    const hairBob = composeTRS(
      [0, 1.55 * sH, 0],
      null,
      [sHead * 1.15, sHead * 0.7, sHead * 1.05]
    );
    const hairTopknot = composeTRS(
      [0, 1.78 * sH, 0],
      null,
      [sHead * 0.55, sHead * 0.55, sHead * 0.55]
    );
    const hairHood = composeTRS(
      [0, 1.60 * sH, 0],
      null,
      [sHead * 1.25, sHead * 1.25, sHead * 1.25]
    );

    return {
      yFootOffset: 0, // ground-level — figure cone bottom is already at y=0
      robe,
      sash,
      shoulders,
      head,
      capDisc,
      capPlank,
      capTassel,
      hairBob,
      hairTopknot,
      hairHood,
    };
  });
  return result;
}

function composeTRS(t, rEuler, s) {
  const m = new THREE.Matrix4();
  const pos = new THREE.Vector3(t[0], t[1], t[2]);
  const quat = new THREE.Quaternion();
  if (rEuler) {
    quat.setFromEuler(new THREE.Euler(rEuler[0], rEuler[1], rEuler[2]));
  }
  const sc = new THREE.Vector3(s[0], s[1], s[2]);
  m.compose(pos, quat, sc);
  return m;
}

/* ------------------------------- Component ------------------------------- */

export default function NPCs() {
  const specs = useMemo(buildNpcSpecs, []);
  const archMats = useMemo(buildArchetypeMatrices, []);
  const dayRef = useDayFactor();
  const densityRef = useCrowdDensity();

  // Mutable per-NPC state — plain typed arrays / arrays for perf.
  const state = useMemo(() => {
    const posX = new Float32Array(MAX_NPCS);
    const posZ = new Float32Array(MAX_NPCS);
    const tgtX = new Float32Array(MAX_NPCS);
    const tgtZ = new Float32Array(MAX_NPCS);
    const facing = new Float32Array(MAX_NPCS);
    const leg = new Float32Array(MAX_NPCS);
    const pauseUntil = new Float32Array(MAX_NPCS);
    const waypointIdx = new Uint8Array(MAX_NPCS);
    const inNightMode = new Uint8Array(MAX_NPCS);
    for (let i = 0; i < MAX_NPCS; i++) {
      const s = specs[i];
      posX[i] = s.startX;
      posZ[i] = s.startZ;
      tgtX[i] = s.dayPath[1 % s.dayPath.length][0];
      tgtZ[i] = s.dayPath[1 % s.dayPath.length][2];
      waypointIdx[i] = 1 % s.dayPath.length;
      facing[i] = 0;
    }
    return { posX, posZ, tgtX, tgtZ, facing, leg, pauseUntil, waypointIdx, inNightMode };
  }, [specs]);

  // Refs to each instanced mesh — we update matrices each frame.
  const robeRef = useRef();
  const sashRef = useRef();
  const shouldersRef = useRef();
  const headRef = useRef();
  const capDiscRef = useRef();
  const capPlankRef = useRef();
  const capTasselRef = useRef();
  const hairBobRef = useRef();
  const hairTopknotRef = useRef();
  const hairHoodRef = useRef();

  // Group NPCs by hair style so each hair InstancedMesh only allocates the
  // slots it actually uses. Returns { mortar:[...indices], bob:[...], ... }
  const byHair = useMemo(() => {
    const o = { mortar: [], bob: [], topknot: [], hood: [], bare: [] };
    specs.forEach((s) => o[s.hair].push(s.i));
    return o;
  }, [specs]);

  // Set instance colors once on mount (and whenever specs change — never).
  useEffect(() => {
    if (!robeRef.current) return;
    const robeColor = new THREE.Color();
    const skinColor = new THREE.Color();
    const sashColor = new THREE.Color();
    const hairColor = new THREE.Color();

    for (let i = 0; i < MAX_NPCS; i++) {
      const p = PALETTES[specs[i].palIdx];
      robeColor.set(p.robe);
      skinColor.set(p.skin);
      sashColor.set(p.sash);

      robeRef.current.setColorAt(i, robeColor);
      shouldersRef.current.setColorAt(i, robeColor);
      capDiscRef.current.setColorAt(i, robeColor);
      capPlankRef.current.setColorAt(i, robeColor);
      sashRef.current.setColorAt(i, sashColor);
      capTasselRef.current.setColorAt(i, sashColor);
      headRef.current.setColorAt(i, skinColor);
    }

    // Hair InstancedMeshes — index is per-mesh local. Map global → local idx.
    const hairColors = {
      bob: hairBobRef.current,
      topknot: hairTopknotRef.current,
      hood: hairHoodRef.current,
    };
    Object.entries(byHair).forEach(([hair, indices]) => {
      const mesh = hairColors[hair];
      if (!mesh) return;
      indices.forEach((globalIdx, localIdx) => {
        hairColor.set(PALETTES[specs[globalIdx].palIdx].hair);
        mesh.setColorAt(localIdx, hairColor);
      });
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    });

    [robeRef, sashRef, shouldersRef, headRef, capDiscRef, capPlankRef, capTasselRef].forEach(
      (r) => {
        if (r.current && r.current.instanceColor) {
          r.current.instanceColor.needsUpdate = true;
        }
      }
    );
  }, [specs, byHair]);

  /* ----------------------- Per-frame simulation + draw ---------------------- */

  // Scratch objects reused across NPCs and parts to avoid allocations.
  const scratch = useMemo(
    () => ({
      npcMat: new THREE.Matrix4(),
      worldMat: new THREE.Matrix4(),
      hiddenMat: (() => {
        const m = new THREE.Matrix4();
        m.makeScale(0, 0, 0);
        return m;
      })(),
      euler: new THREE.Euler(),
      quat: new THREE.Quaternion(),
      pos: new THREE.Vector3(),
      scl: new THREE.Vector3(1, 1, 1),
    }),
    []
  );

  useFrame((threeState, delta) => {
    if (!robeRef.current) return;
    const t = threeState.clock.elapsedTime;
    const f = THREE.MathUtils.clamp(dayRef.current, 0, 1);

    // Dynamic population cap. Day=100, Night=MIN_NPCS_NIGHT.
    const targetCount = Math.round(MIN_NPCS_NIGHT + (MAX_NPCS - MIN_NPCS_NIGHT) * f);
    // Use night routing once it's mostly night.
    const useNightRouting = f < 0.45;

    const robe = robeRef.current;
    const sash = sashRef.current;
    const shoulders = shouldersRef.current;
    const head = headRef.current;
    const capDisc = capDiscRef.current;
    const capPlank = capPlankRef.current;
    const capTassel = capTasselRef.current;
    const hairBob = hairBobRef.current;
    const hairTopknot = hairTopknotRef.current;
    const hairHood = hairHoodRef.current;

    // Per-hair local indices we increment as we touch each NPC.
    const hairCursor = { bob: 0, topknot: 0, hood: 0 };

    const { npcMat, worldMat, hiddenMat, euler, quat, pos, scl } = scratch;
    const dt = Math.min(0.05, delta); // clamp dt to avoid huge jumps on stalls

    for (let i = 0; i < MAX_NPCS; i++) {
      const spec = specs[i];
      // Skip hidden NPCs entirely (night cull). Hide all of their parts.
      const visible = i < targetCount;
      if (!visible) {
        robe.setMatrixAt(i, hiddenMat);
        sash.setMatrixAt(i, hiddenMat);
        shoulders.setMatrixAt(i, hiddenMat);
        head.setMatrixAt(i, hiddenMat);
        // Hide cap/hair parts associated with this NPC too.
        if (spec.hair === "mortar") {
          capDisc.setMatrixAt(i, hiddenMat);
          capPlank.setMatrixAt(i, hiddenMat);
          capTassel.setMatrixAt(i, hiddenMat);
        } else {
          // Even non-mortar NPCs share these slots — hide them defensively.
          capDisc.setMatrixAt(i, hiddenMat);
          capPlank.setMatrixAt(i, hiddenMat);
          capTassel.setMatrixAt(i, hiddenMat);
        }
        if (spec.hair === "bob") hairBob.setMatrixAt(hairCursor.bob++, hiddenMat);
        else if (spec.hair === "topknot") hairTopknot.setMatrixAt(hairCursor.topknot++, hiddenMat);
        else if (spec.hair === "hood") hairHood.setMatrixAt(hairCursor.hood++, hiddenMat);
        continue;
      }

      // Pick path + decide whether to switch routing mode.
      const wantNight = useNightRouting && spec.nightActive;
      if (state.inNightMode[i] !== (wantNight ? 1 : 0)) {
        // Reset target/idx to first waypoint in the new mode.
        state.inNightMode[i] = wantNight ? 1 : 0;
        const path = wantNight ? spec.nightPath : spec.dayPath;
        state.waypointIdx[i] = 0;
        state.tgtX[i] = path[0][0];
        state.tgtZ[i] = path[0][2];
        state.pauseUntil[i] = 0;
      }

      const path = state.inNightMode[i] ? spec.nightPath : spec.dayPath;
      const pauseUntil = state.pauseUntil[i];
      let walking = false;

      if (t < pauseUntil) {
        // pausing — small idle sway in facing
        state.facing[i] += Math.sin(t * 0.6 + spec.phase) * 0.0008;
      } else {
        const dx = state.tgtX[i] - state.posX[i];
        const dz = state.tgtZ[i] - state.posZ[i];
        const dist = Math.sqrt(dx * dx + dz * dz);
        if (dist < 0.22) {
          // Reached. Pause briefly then choose next waypoint.
          state.pauseUntil[i] = t + 0.4 + (Math.sin(i + t * 0.1) + 1) * 0.6;
          const next = (state.waypointIdx[i] + 1) % path.length;
          state.waypointIdx[i] = next;
          state.tgtX[i] = path[next][0];
          state.tgtZ[i] = path[next][2];
        } else {
          const step = spec.speed * dt;
          const nx = dx / dist;
          const nz = dz / dist;
          state.posX[i] += nx * step;
          state.posZ[i] += nz * step;
          // Face direction (lerp).
          const desired = Math.atan2(nx, nz);
          let diff = desired - state.facing[i];
          while (diff > Math.PI) diff -= Math.PI * 2;
          while (diff < -Math.PI) diff += Math.PI * 2;
          state.facing[i] += diff * Math.min(1, dt * 6);
          state.leg[i] += dt * 6;
          walking = true;
        }
      }

      const bob = walking
        ? Math.abs(Math.sin(state.leg[i])) * 0.05
        : Math.sin(t * 1.4 + spec.phase) * 0.012;

      // Build NPC root matrix: T(pos+bob) * R(facing,Y) * S(1)
      pos.set(state.posX[i], bob, state.posZ[i]);
      euler.set(0, state.facing[i], 0);
      quat.setFromEuler(euler);
      scl.set(1, 1, 1);
      npcMat.compose(pos, quat, scl);

      const aMats = archMats[spec.archIdx];

      // For each body part: world = npcMat * archetypeLocal
      worldMat.multiplyMatrices(npcMat, aMats.robe);
      robe.setMatrixAt(i, worldMat);
      worldMat.multiplyMatrices(npcMat, aMats.sash);
      sash.setMatrixAt(i, worldMat);
      worldMat.multiplyMatrices(npcMat, aMats.shoulders);
      shoulders.setMatrixAt(i, worldMat);
      worldMat.multiplyMatrices(npcMat, aMats.head);
      head.setMatrixAt(i, worldMat);

      // Mortarboard cap — only NPCs with hair="mortar" get a visible cap;
      // others get the zero-scale hidden matrix in those slots.
      if (spec.hair === "mortar") {
        worldMat.multiplyMatrices(npcMat, aMats.capDisc);
        capDisc.setMatrixAt(i, worldMat);
        worldMat.multiplyMatrices(npcMat, aMats.capPlank);
        capPlank.setMatrixAt(i, worldMat);
        worldMat.multiplyMatrices(npcMat, aMats.capTassel);
        capTassel.setMatrixAt(i, worldMat);
      } else {
        capDisc.setMatrixAt(i, hiddenMat);
        capPlank.setMatrixAt(i, hiddenMat);
        capTassel.setMatrixAt(i, hiddenMat);
      }

      // Hair variants — one of three meshes; each uses a per-hair local index.
      if (spec.hair === "bob") {
        worldMat.multiplyMatrices(npcMat, aMats.hairBob);
        hairBob.setMatrixAt(hairCursor.bob++, worldMat);
      } else if (spec.hair === "topknot") {
        worldMat.multiplyMatrices(npcMat, aMats.hairTopknot);
        hairTopknot.setMatrixAt(hairCursor.topknot++, worldMat);
      } else if (spec.hair === "hood") {
        worldMat.multiplyMatrices(npcMat, aMats.hairHood);
        hairHood.setMatrixAt(hairCursor.hood++, worldMat);
      }
      // "bare" / "mortar" NPCs have no extra hair mesh — handled above.
    }

    robe.instanceMatrix.needsUpdate = true;
    sash.instanceMatrix.needsUpdate = true;
    shoulders.instanceMatrix.needsUpdate = true;
    head.instanceMatrix.needsUpdate = true;
    capDisc.instanceMatrix.needsUpdate = true;
    capPlank.instanceMatrix.needsUpdate = true;
    capTassel.instanceMatrix.needsUpdate = true;
    if (hairBob.count > 0) hairBob.instanceMatrix.needsUpdate = true;
    if (hairTopknot.count > 0) hairTopknot.instanceMatrix.needsUpdate = true;
    if (hairHood.count > 0) hairHood.instanceMatrix.needsUpdate = true;
  });

  // Hair pool sizes (one InstancedMesh per hair style, sized to exact count).
  const bobCount = byHair.bob.length;
  const topknotCount = byHair.topknot.length;
  const hoodCount = byHair.hood.length;

  return (
    <group data-tab-role="npcs">
      {/* Robe (cone) */}
      <instancedMesh
        ref={robeRef}
        args={[undefined, undefined, MAX_NPCS]}
        frustumCulled={false}
        castShadow
      >
        <coneGeometry args={[0.45, 1.2, 10]} />
        <meshStandardMaterial roughness={0.85} />
      </instancedMesh>

      {/* Sash (torus) */}
      <instancedMesh
        ref={sashRef}
        args={[undefined, undefined, MAX_NPCS]}
        frustumCulled={false}
      >
        <torusGeometry args={[0.32, 0.04, 6, 12]} />
        <meshStandardMaterial metalness={0.5} roughness={0.4} />
      </instancedMesh>

      {/* Shoulders (sphere) */}
      <instancedMesh
        ref={shouldersRef}
        args={[undefined, undefined, MAX_NPCS]}
        frustumCulled={false}
        castShadow
      >
        <sphereGeometry args={[0.27, 10, 8]} />
        <meshStandardMaterial roughness={0.85} />
      </instancedMesh>

      {/* Head (sphere) */}
      <instancedMesh
        ref={headRef}
        args={[undefined, undefined, MAX_NPCS]}
        frustumCulled={false}
      >
        <sphereGeometry args={[0.18, 12, 10]} />
        <meshStandardMaterial roughness={0.85} />
      </instancedMesh>

      {/* Mortarboard disc / plank / tassel — one slot per NPC, hidden if hair!=mortar */}
      <instancedMesh
        ref={capDiscRef}
        args={[undefined, undefined, MAX_NPCS]}
        frustumCulled={false}
      >
        <cylinderGeometry args={[0.15, 0.15, 0.05, 10]} />
        <meshStandardMaterial roughness={0.85} />
      </instancedMesh>
      <instancedMesh
        ref={capPlankRef}
        args={[undefined, undefined, MAX_NPCS]}
        frustumCulled={false}
      >
        <boxGeometry args={[0.42, 0.03, 0.42]} />
        <meshStandardMaterial roughness={0.85} />
      </instancedMesh>
      <instancedMesh
        ref={capTasselRef}
        args={[undefined, undefined, MAX_NPCS]}
        frustumCulled={false}
      >
        <cylinderGeometry args={[0.02, 0.02, 0.18, 6]} />
        <meshStandardMaterial color={COLORS.gold} metalness={0.4} />
      </instancedMesh>

      {/* Hair variants — each sized to its own pool count */}
      {bobCount > 0 && (
        <instancedMesh
          ref={hairBobRef}
          args={[undefined, undefined, bobCount]}
          frustumCulled={false}
        >
          <boxGeometry args={[0.32, 0.18, 0.30]} />
          <meshStandardMaterial roughness={0.9} />
        </instancedMesh>
      )}
      {topknotCount > 0 && (
        <instancedMesh
          ref={hairTopknotRef}
          args={[undefined, undefined, topknotCount]}
          frustumCulled={false}
        >
          <sphereGeometry args={[0.10, 10, 8]} />
          <meshStandardMaterial roughness={0.9} />
        </instancedMesh>
      )}
      {hoodCount > 0 && (
        <instancedMesh
          ref={hairHoodRef}
          args={[undefined, undefined, hoodCount]}
          frustumCulled={false}
        >
          <sphereGeometry args={[0.22, 10, 8]} />
          <meshStandardMaterial roughness={0.95} />
        </instancedMesh>
      )}
    </group>
  );
}
